/**
 * Article Image Gallery Handler
 * Processes [image-gallery] blocks and creates modal slideshow
 */

class ArticleImageGallery {
  constructor() {
    this.contentElement = document.querySelector('.article-template__content');
    this.modal = null;
    this.slideshow = null;
    this.slideshowElement = null;
    this.allImages = [];
    this.imageIndexMap = new Map(); // Maps image src to index in allImages array
    this.isMobile = window.innerWidth < 700; // Match CSS breakpoint
    this.mobileMediaQuery = window.matchMedia('(max-width: 699px)');
    this.currentView = 'grid'; // 'grid' or 'slideshow'
    this.buttonHandlerAttached = false; // Track if event handler is attached
    
    if (!this.contentElement) return;
    
    this.init();
    
    // Listen for resize to handle mobile/desktop switching
    this.mobileMediaQuery.addEventListener('change', () => {
      this.isMobile = this.mobileMediaQuery.matches;
      this.handleResponsiveGalleries();
    });
  }

  init() {
    this.processImageGalleries();
    this.collectAllImages();
    this.setupModal();
    this.setupViewToggle();
    this.setupGalleryWidget();
  }

  /**
   * Get column count based on image count
   */
  getColumns(imageCount) {
    if (imageCount === 1) return 1;
    if (imageCount === 2) return 2;
    if (imageCount === 3) return 3;
    if (imageCount === 4) return 2;
    if (imageCount === 6) return 3;
    return 2; // default
  }

  /**
   * Process [image-gallery] blocks in content
   */
  processImageGalleries() {
    const content = this.contentElement.innerHTML;
    
    // Find all [image-gallery]...[/image-gallery] blocks (case-insensitive, handles whitespace)
    // Updated regex to capture attributes from the opening tag
    const galleryRegex = /\[image-gallery\s*([^\]]*)\]([\s\S]*?)\[\/image-gallery\]/gi;
    const matches = [...content.matchAll(galleryRegex)];
    
    if (matches.length === 0) return;

    let updatedContent = content;
    
    matches.forEach((match, matchIndex) => {
      const galleryAttributes = match[1].trim();
      const galleryContent = match[2].trim();
      const fullMatch = match[0];
      
      // Check if data-small-single-image attribute is present
      const hasSmallSingleImage = galleryAttributes.includes('data-small-single-image');
      
      // Create temporary container to parse HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = galleryContent;
      
      // Find all images in this gallery
      const images = tempDiv.querySelectorAll('img');
      
      if (images.length === 0) {
        // If no images found, remove the gallery tags but keep content
        updatedContent = updatedContent.replace(fullMatch, galleryContent);
        return;
      }
      
      // Store original images data for later use
      const imageData = Array.from(images).map((img) => {
        const clonedImg = img.cloneNode(true);
        const imageSrc = clonedImg.src || 
                        clonedImg.getAttribute('src') || 
                        clonedImg.getAttribute('srcset')?.split(' ')[0] ||
                        clonedImg.getAttribute('data-src') ||
                        '';
        return { img: clonedImg, src: imageSrc };
      });
      
      // Create gallery container with data attribute for tracking
      const galleryContainer = document.createElement('div');
      galleryContainer.className = 'article-image-gallery-wrapper';
      galleryContainer.setAttribute('data-gallery-index', matchIndex);
      galleryContainer.setAttribute('data-image-count', images.length);
      
      // For 3+ images: create both mobile slideshow and desktop grid
      // For 2 images: create grid only (works on both mobile and desktop)
      if (images.length > 2) {
        this.createMobileSlideshow(galleryContainer, imageData, hasSmallSingleImage);
      } else {
        this.createGridGallery(galleryContainer, images.length, imageData, hasSmallSingleImage);
      }
      
      // Replace the [image-gallery] block in the content
      updatedContent = updatedContent.replace(fullMatch, galleryContainer.outerHTML);
    });
    
    // Update content once with all replacements
    this.contentElement.innerHTML = updatedContent;
    
    // Initialize mobile slideshows if needed
    this.initializeMobileSlideshows();
    
    // Add click handlers to gallery buttons
    this.attachGalleryButtonHandlers();
  }

  /**
   * Create grid gallery layout
   */
  createGridGallery(container, imageCount, imageData, hasSmallSingleImage = false) {
    const columnCount = this.getColumns(imageCount);
    const galleryGrid = document.createElement('div');
    let className = `article-image-gallery article-image-gallery--cols-${columnCount}`;
    
    // Add is-small class if it's a single column and has the data-small-single-image attribute
    if (columnCount === 1 && hasSmallSingleImage) {
      className += ' is-small';
    }
    
    galleryGrid.className = className;
    
    imageData.forEach((data, imgIndex) => {
      const galleryItem = document.createElement('div');
      galleryItem.className = 'article-image-gallery__item';
      
      const clonedImg = data.img.cloneNode(true);
      clonedImg.className = 'article-image-gallery__image';
      
      // Create button wrapper
      const button = document.createElement('button');
      button.className = 'article-image-gallery__button';
      button.type = 'button';
      button.setAttribute('data-image-src', data.src);
      button.setAttribute('aria-label', `View image ${imgIndex + 1} in gallery`);
      
      button.appendChild(clonedImg);
      galleryItem.appendChild(button);
      galleryGrid.appendChild(galleryItem);
    });
    
    container.appendChild(galleryGrid);
  }

  /**
   * Create mobile Swiper slideshow for galleries with 3+ images
   */
  createMobileSlideshow(container, imageData, hasSmallSingleImage = false) {
    const slideshow = document.createElement('custom-slideshow');
    slideshow.className = 'article-image-gallery-mobile-slideshow';
    slideshow.setAttribute('slidesPerView', '2');
    slideshow.setAttribute('pagination', 'true');
    slideshow.setAttribute('navigation', 'true');
    slideshow.setAttribute('loop', 'false');
    slideshow.setAttribute('spaceBetween', '20');
    slideshow.setAttribute('disableOn', 'mediumUp'); // Disable on desktop, show grid instead
    
    imageData.forEach((data, imgIndex) => {
      const slide = document.createElement('div');
      slide.className = 'article-image-gallery-mobile-slide';
      
      const galleryItem = document.createElement('div');
      galleryItem.className = 'article-image-gallery__item';
      
      const clonedImg = data.img.cloneNode(true);
      clonedImg.className = 'article-image-gallery__image';
      
      // Create button wrapper
      const button = document.createElement('button');
      button.className = 'article-image-gallery__button';
      button.type = 'button';
      button.setAttribute('data-image-src', data.src);
      button.setAttribute('aria-label', `View image ${imgIndex + 1} in gallery`);
      
      button.appendChild(clonedImg);
      galleryItem.appendChild(button);
      slide.appendChild(galleryItem);
      slideshow.appendChild(slide);
    });
    
    container.appendChild(slideshow);
    
    // Also create grid version for desktop (hidden on mobile)
    const galleryGrid = document.createElement('div');
    const columnCount = this.getColumns(imageData.length);
    let className = `article-image-gallery article-image-gallery--desktop article-image-gallery--cols-${columnCount}`;
    
    // Add is-small class if it's a single column and has the data-small-single-image attribute
    if (columnCount === 1 && hasSmallSingleImage) {
      className += ' is-small';
    }
    
    galleryGrid.className = className;
    
    imageData.forEach((data, imgIndex) => {
      const galleryItem = document.createElement('div');
      galleryItem.className = 'article-image-gallery__item';
      
      const clonedImg = data.img.cloneNode(true);
      clonedImg.className = 'article-image-gallery__image';
      
      const button = document.createElement('button');
      button.className = 'article-image-gallery__button';
      button.type = 'button';
      button.setAttribute('data-image-src', data.src);
      button.setAttribute('aria-label', `View image ${imgIndex + 1} in gallery`);
      
      button.appendChild(clonedImg);
      galleryItem.appendChild(button);
      galleryGrid.appendChild(galleryItem);
    });
    
    container.appendChild(galleryGrid);
  }

  /**
   * Initialize mobile slideshows
   */
  initializeMobileSlideshows() {
    // The custom-slideshow component will auto-initialize
    // We just need to make sure it's loaded
  }

  /**
   * Handle responsive gallery switching
   */
  handleResponsiveGalleries() {
    // This will be called when screen size changes
    // For now, we'll let CSS handle the visibility
    // The slideshow component handles its own responsive behavior via disableOn attribute
  }

  /**
   * Attach click handlers to gallery buttons using event delegation
   * This ensures handlers persist even if innerHTML is modified by other scripts
   */
  attachGalleryButtonHandlers() {
    // Only attach handler once
    if (this.buttonHandlerAttached) return;
    
    // Use event delegation on the content element
    // This way handlers persist even if innerHTML is replaced
    this.contentElement.addEventListener('click', (e) => {
      // Check if the clicked element or its parent is a gallery button
      const button = e.target.closest('.article-image-gallery__button');
      if (!button) return;
      
      e.preventDefault();
      e.stopPropagation();
      
      const imageSrc = button.getAttribute('data-image-src');
      // Try exact match first, then normalized match
      let imageIndex = this.imageIndexMap.get(imageSrc);
      if (imageIndex === undefined && imageSrc) {
        // Try to normalize and find
        try {
          const url = new URL(imageSrc, window.location.origin);
          const normalizedSrc = url.origin + url.pathname;
          imageIndex = this.imageIndexMap.get(normalizedSrc);
        } catch {
          // If URL parsing fails, try simple normalization
          const normalizedSrc = imageSrc.split('?')[0].split('#')[0];
          imageIndex = this.imageIndexMap.get(normalizedSrc);
        }
      }
      if (imageIndex !== undefined) {
        // Open in slideshow view when clicking from gallery
        this.openModal(imageIndex, 'slideshow');
      } else {
        // Fallback: open modal at first image
        this.openModal(0, 'slideshow');
      }
    });
    
    this.buttonHandlerAttached = true;
  }

  /**
   * Collect all images from the entire blog post
   */
  collectAllImages() {
    // Get all images from the content area (after processing galleries)
    const contentImages = this.contentElement.querySelectorAll('img');
    
    // Also check for featured image if it exists
    const featuredImage = document.querySelector('.article-template__hero-container img');
    
    // Helper to get image source
    const getImageSrc = (img) => {
      return img.src || 
             img.getAttribute('src') || 
             img.getAttribute('srcset')?.split(' ')[0] ||
             img.getAttribute('data-src') ||
             '';
    };
    
    // Helper to normalize src for comparison (remove query params and fragments)
    const normalizeSrc = (src) => {
      try {
        const url = new URL(src, window.location.origin);
        return url.origin + url.pathname;
      } catch {
        return src.split('?')[0].split('#')[0];
      }
    };
    
    // Collect content images
    contentImages.forEach((img) => {
      const src = getImageSrc(img);
      if (src) {
        const normalizedSrc = normalizeSrc(src);
        if (!this.allImages.some(existing => normalizeSrc(existing.src) === normalizedSrc)) {
          this.allImages.push({
            src: src,
            alt: img.alt || '',
            width: img.width || img.getAttribute('width') || '',
            height: img.height || img.getAttribute('height') || ''
          });
          this.imageIndexMap.set(src, this.allImages.length - 1);
          this.imageIndexMap.set(normalizedSrc, this.allImages.length - 1);
        }
      }
    });
    
    // Add featured image at the beginning if it exists
    if (featuredImage) {
      const src = getImageSrc(featuredImage);
      if (src) {
        const normalizedSrc = normalizeSrc(src);
        if (!this.allImages.some(existing => normalizeSrc(existing.src) === normalizedSrc)) {
          this.allImages.unshift({
            src: src,
            alt: featuredImage.alt || '',
            width: featuredImage.width || featuredImage.getAttribute('width') || '',
            height: featuredImage.height || featuredImage.getAttribute('height') || ''
          });
          // Rebuild index map
          this.imageIndexMap.clear();
          this.allImages.forEach((img, index) => {
            this.imageIndexMap.set(img.src, index);
            this.imageIndexMap.set(normalizeSrc(img.src), index);
          });
        }
      }
    }
  }

  /**
   * Setup modal structure
   */
  setupModal() {
    if (this.allImages.length === 0) return;
    
    // Check if modal already exists
    this.modal = document.querySelector('#article-image-modal');
    if (!this.modal) {
      console.warn('Article image modal not found in HTML');
      return;
    }
    
    // Populate slideshow with images
    this.populateSlideshow();
  }

  /**
   * Populate modal grid with all images
   */
  populateSlideshow() {
    const grid = document.querySelector('#article-image-modal-grid');
    if (!grid) return;
    
    // Clear existing content
    grid.innerHTML = '';
    
    // Add each image as a grid item
    this.allImages.forEach((image, index) => {
      const gridItem = document.createElement('div');
      gridItem.className = 'article-image-gallery__item';
      gridItem.style.pointerEvents = 'auto';
      
      const button = document.createElement('button');
      button.className = 'article-image-gallery__button';
      button.type = 'button';
      button.setAttribute('data-image-src', image.src);
      button.setAttribute('aria-label', `View image ${index + 1} in gallery`);
      button.setAttribute('data-image-index', index);
      button.style.pointerEvents = 'auto';
      
      // Add click handler to switch to slideshow view
      button.addEventListener('click', () => {
        this.switchToView('slideshow', index);
      });
      
      const img = document.createElement('img');
      img.src = image.src;
      img.alt = image.alt || '';
      if (image.width) img.width = image.width;
      if (image.height) img.height = image.height;
      img.loading = 'lazy';
      img.className = 'article-image-gallery__image';
      img.style.pointerEvents = 'auto';
      
      button.appendChild(img);
      gridItem.appendChild(button);
      grid.appendChild(gridItem);
    });
  }

  /**
   * Setup view toggle buttons
   */
  setupViewToggle() {
    const toggleButtons = document.querySelectorAll('.article-image-modal__toggle-button');
    if (!toggleButtons.length) return;
    
    toggleButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const view = button.getAttribute('data-view');
        this.switchToView(view);
      });
    });
  }

  /**
   * Setup gallery widget buttons and count
   */
  setupGalleryWidget() {
    const galleryWidget = document.querySelector('[data-gallery-widget]');
    if (!galleryWidget) return;

    // Set the image count
    const countElement = galleryWidget.querySelector('[data-gallery-count]');
    if (countElement) {
      countElement.textContent = this.allImages.length;
    }

    // Setup click handlers for action buttons
    const slideshowButton = galleryWidget.querySelector('[data-action="slideshow"]');
    const gridButton = galleryWidget.querySelector('[data-action="grid"]');

    if (slideshowButton) {
      slideshowButton.addEventListener('click', (e) => {
        e.preventDefault();
        // Open modal in slideshow view starting at first image
        this.openModal(0, 'slideshow');
      });
    }

    if (gridButton) {
      gridButton.addEventListener('click', (e) => {
        e.preventDefault();
        // Open modal in grid view
        this.openModal(0, 'grid');
      });
    }
  }

  /**
   * Switch between grid and slideshow views
   */
  switchToView(view, imageIndex = null) {
    if (view !== 'grid' && view !== 'slideshow') return;
    
    this.currentView = view;
    const gridWrapper = document.querySelector('.article-image-modal__grid-wrapper');
    const slideshowContainer = document.querySelector('#article-image-modal-slideshow');
    const toggleButtons = document.querySelectorAll('.article-image-modal__toggle-button');
    const infoSection = document.querySelector('#article-image-modal-info');
    
    if (!gridWrapper || !slideshowContainer) return;
    
    // Update button states
    toggleButtons.forEach((button) => {
      if (button.getAttribute('data-view') === view) {
        button.classList.add('is-active');
      } else {
        button.classList.remove('is-active');
      }
    });
    
    // Show/hide views
    if (view === 'grid') {
      gridWrapper.classList.remove('is-hidden');
      slideshowContainer.classList.add('is-hidden');
      // Hide info section in grid view
      if (infoSection) {
        infoSection.classList.add('is-hidden');
      }
    } else {
      gridWrapper.classList.add('is-hidden');
      slideshowContainer.classList.remove('is-hidden');
      // Show info section in slideshow view
      if (infoSection) {
        infoSection.classList.remove('is-hidden');
      }
      
      // Initialize slideshow if not already done
      if (!this.slideshowElement) {
        this.initializeSlideshow();
      }
      
      // Navigate to specific image if provided
      if (imageIndex !== null && typeof imageIndex === 'number') {
        this.navigateToSlide(imageIndex);
      } else if (this.slideshow) {
        // Update sidebar with current slide
        this.updateSidebarInfo(this.slideshow.activeIndex);
      }
    }
  }

  /**
   * Initialize slideshow with all images
   */
  initializeSlideshow() {
    const slideshowContainer = document.querySelector('#article-image-modal-slideshow');
    if (!slideshowContainer || this.slideshowElement) return;
    
    // Clear existing content
    slideshowContainer.innerHTML = '';
    
    // Create slides HTML first
    const slidesHTML = this.allImages.map((image, index) => {
      const widthAttr = image.width ? ` width="${image.width}"` : '';
      const heightAttr = image.height ? ` height="${image.height}"` : '';
      const altText = image.alt || '';
      return `
        <div class="article-image-modal__slide" data-slide-index="${index}">
          <img 
            src="${image.src}" 
            alt="${altText}"${widthAttr}${heightAttr}
            loading="${index === 0 ? 'eager' : 'lazy'}"
            class="article-image-modal__slide-image"
          />
          ${altText ? `<div class="article-image-modal__slide-alt">${altText}</div>` : ''}
        </div>
      `;
    }).join('');
    
    // Create element with attributes in the HTML string so constructor can read them
    slideshowContainer.innerHTML = `
      <custom-slideshow 
        class="article-image-modal__slideshow-element"
        slidesPerView="1"
        pagination="false"
        autoHeight="true"
        navigation="${this.allImages.length > 1 ? 'true' : 'false'}"
        loop="false"
        effect="fade"
      >
        ${slidesHTML}
      </custom-slideshow>
    `;
    
    this.slideshowElement = slideshowContainer.querySelector('custom-slideshow');
    
    // Listen for swiperLoaded event to get the Swiper instance
    this.slideshowElement.addEventListener('swiperLoaded', (event) => {
      if (event.detail && event.detail.swiper) {
        this.slideshow = event.detail.swiper;
        this.setupSlideshowTracking();
      } else if (this.slideshowElement.swiper) {
        this.slideshow = this.slideshowElement.swiper;
        this.setupSlideshowTracking();
      }
    });
    
    // Fallback: get Swiper instance after a delay
    setTimeout(() => {
      if (this.slideshowElement.swiper && !this.slideshow) {
        this.slideshow = this.slideshowElement.swiper;
        this.setupSlideshowTracking();
      }
    }, 500);
  }

  /**
   * Setup Swiper event listeners to track slide changes
   */
  setupSlideshowTracking() {
    if (!this.slideshow) return;
    
    // Listen to slide changes
    this.slideshow.on('slideChange', () => {
      const activeIndex = this.slideshow.activeIndex;
      this.updateSidebarInfo(activeIndex);
    });
    
    // Also listen to transition end for more reliable tracking
    this.slideshow.on('slideChangeTransitionEnd', () => {
      const activeIndex = this.slideshow.activeIndex;
      this.updateSidebarInfo(activeIndex);
    });
    
    // Initialize with current slide
    this.updateSidebarInfo(this.slideshow.activeIndex);
  }

  /**
   * Update sidebar with current image info (slideshow only)
   */
  updateSidebarInfo(imageIndex) {
    if (imageIndex < 0 || imageIndex >= this.allImages.length) return;
    
    const infoSection = document.querySelector('#article-image-modal-info');
    const currentIndexEl = document.querySelector('.article-image-modal__current-index');
    const totalCountEl = document.querySelector('.article-image-modal__total-count');
    const altTextEl = document.querySelector('.article-image-modal__image-alt');
    
    if (!infoSection) return;
    
    // Update counter
    if (currentIndexEl) {
      currentIndexEl.textContent = imageIndex + 1;
    }
    if (totalCountEl) {
      totalCountEl.textContent = this.allImages.length;
    }
    
    // Update alt text
    if (altTextEl) {
      const alt = this.allImages[imageIndex].alt || '';
      altTextEl.textContent = alt;
      altTextEl.style.display = alt ? 'block' : 'none';
    }
  }

  /**
   * Navigate to specific slide in slideshow
   */
  navigateToSlide(index) {
    if (!this.slideshowElement) {
      this.initializeSlideshow();
      // Wait for initialization
      setTimeout(() => {
        this.navigateToSlide(index);
      }, 400);
      return;
    }
    
    // Try to get Swiper instance
    if (!this.slideshow && this.slideshowElement.swiper) {
      this.slideshow = this.slideshowElement.swiper;
    }
    
    if (this.slideshow && typeof this.slideshow.slideTo === 'function') {
      this.slideshow.slideTo(index, 300);
    } else {
      // Fallback: wait a bit more and try again
      setTimeout(() => {
        if (this.slideshowElement.swiper) {
          this.slideshow = this.slideshowElement.swiper;
          if (this.slideshow && typeof this.slideshow.slideTo === 'function') {
            this.slideshow.slideTo(index, 300);
          }
        }
      }, 200);
    }
  }

  /**
   * Open modal at specific image index
   */
  openModal(imageIndex, view = 'grid') {
    if (!this.modal) return;
    
    const globalModal = this.modal;
    const dialog = globalModal.querySelector('dialog');
    
    // Call openModal on the GlobalModal instance with null event
    if (globalModal.openModal) {
      globalModal.openModal(null);
    }
    
    // Ensure native dialog is opened
    if (dialog && dialog.showModal && !dialog.hasAttribute('open')) {
      dialog.showModal();
    }
    
    // Set the view
    this.switchToView(view, view === 'slideshow' ? imageIndex : null);
    
    // If grid view, scroll to the clicked image
    if (view === 'grid') {
      const scrollToImage = () => {
        const grid = document.querySelector('#article-image-modal-grid');
        if (grid && typeof imageIndex === 'number') {
          const targetButton = grid.querySelector(`.article-image-gallery__button[data-image-index="${imageIndex}"]`);
          if (targetButton) {
            setTimeout(() => {
              targetButton.scrollIntoView({ behavior: 'smooth', block: 'center' });
              // Add a highlight effect
              const gridItem = targetButton.closest('.article-image-gallery__item');
              if (gridItem) {
                gridItem.classList.add('is-highlighted');
                setTimeout(() => {
                  gridItem.classList.remove('is-highlighted');
                }, 1000);
              }
            }, 100);
          }
        }
      };
      
      // Wait a bit for modal to open
      setTimeout(scrollToImage, 200);
    }
  }
}

// Initialize when DOM is ready and custom elements are defined
function initArticleImageGallery() {
  // Wait for custom elements to be defined
  if (customElements.get('global-modal') && customElements.get('custom-slideshow')) {
    new ArticleImageGallery();
  } else {
    // Wait a bit and try again
    setTimeout(initArticleImageGallery, 100);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    setTimeout(initArticleImageGallery, 100);
  });
} else {
  setTimeout(initArticleImageGallery, 100);
}

